import { useEffect, useState } from "react";
import "./card.css";
import { Tooltip } from "primereact/tooltip";

interface BullCardProps {
  bullcard: {
    bullName: string;
    workPercentage: number;
    availableBatchesToday: number;
    completedBatchesToday: number;
    pairedBulls: string;
    operator1: string;
    operator2: string;
    monthlyTotalBatches: number;
    monthlyCompletedBatches: number;
  };
}

const BullCard: React.FC<BullCardProps> = ({ bullcard }) => {
  const [progressWidth, setProgressWidth] = useState(0);

  const [expandedSections, setExpandedSections] = useState({
    pairedBulls: false,
    operators: false,
  });

  const percentage = parseInt(String(bullcard.workPercentage));

  // ---------- Merge Operators ----------
  const mergeOperators = () => {
    const list1 = bullcard.operator1
      ? bullcard.operator1.split(",").map((s) => s.trim())
      : [];

    const list2 = bullcard.operator2
      ? bullcard.operator2.split(",").map((s) => s.trim())
      : [];

    return Array.from(new Set([...list1, ...list2])).filter((x) => x);
  };

  const mergedOperators = mergeOperators();
  const mergedOperatorString = mergedOperators.join(", ");

  // Truncation checks
  const maxLength = 25;
  const shouldHideOperators = mergedOperatorString.length > maxLength;

  const shouldHidePairedBulls =
    bullcard.pairedBulls && bullcard.pairedBulls.length > maxLength;

  const toggleSection = (section: "pairedBulls" | "operators") => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Severity color logic
  const getSeverity = (): "success" | "warning" | "danger" => {
    if (percentage >= 70) return "danger";
    if (percentage >= 40) return "warning";
    return "success";
  };

  const severity = getSeverity();

  useEffect(() => {
    const timer = setTimeout(() => {
      setProgressWidth(percentage);
    }, 100);
    return () => clearTimeout(timer);
  }, [percentage]);

  const getSeverityGradient = (sev: string) => {
    const gradients: { [key: string]: string } = {
      success: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
      warning: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
      danger: "linear-gradient(135deg, #ef4444 0%, #f87171 100%)",
    };
    return gradients[sev] || gradients.danger;
  };

  return (
    <div className="bull-card-wrapper">
      <div
        className="bull-card"
        style={{ background: getSeverityGradient(severity) }}
      >
        <div className="card-content">
          {/* Header */}
          <div className="card-header">
            <div className="card-header-top">
              <h2 className="bull-name">{bullcard.bullName}</h2>

              <span className="percentage-text" data-severity={severity}>
                {bullcard.workPercentage}%
              </span>
            </div>

            <div className="progress-bar-container">
              <div className="progress-bar-track">
                <div
                  className="progress-bar-fill"
                  data-severity={severity}
                  style={{ width: `${progressWidth}%` }}
                />
              </div>
            </div>
          </div>

          {/* Today's Stats */}
          <div className="section-wrapper">
            <div className="section-title">
              <i className="pi pi-calendar section-icon" />
              <span>TODAY</span>
              <span className="section-ratio">
                {bullcard.monthlyCompletedBatches}/{bullcard.monthlyTotalBatches}
                <i
                  className="pi pi-info-circle section-ratio-icon"
                  data-pr-tooltip-id={bullcard.bullName}
                />
              </span>

              <Tooltip target={`[data-pr-tooltip-id="${bullcard.bullName}"]`}>
                <div className="p-3">
                  Monthly Completed: {bullcard.monthlyCompletedBatches}
                  <br />
                  Monthly Total: {bullcard.monthlyTotalBatches}
                </div>
              </Tooltip>
            </div>

            <div className="stats-row">
              <div className="stat-box">
                <i className="pi pi-inbox stat-icon" />
                <div className="stat-info">
                  <span className="stat-number">{bullcard.availableBatchesToday}</span>
                  <span className="stat-label">Available</span>
                </div>
              </div>

              <div className="stat-box">
                <i className="pi pi-check-circle stat-icon" />
                <div className="stat-info">
                  <span className="stat-number">{bullcard.completedBatchesToday}</span>
                  <span className="stat-label">Completed</span>
                </div>
              </div>
            </div>
          </div>

          {/* Info Sections */}
          <div className="info-section">

            {/* Paired Bulls */}
            {bullcard.pairedBulls && (
              <div className="info-card-wrapper">
                <div className="info-card">
                  <div className="info-icon bulls">
                    <i className="pi pi-link" />
                  </div>

                  <div className="info-text">
                    <span className="info-title">Paired Bulls</span>

                    {shouldHidePairedBulls && !expandedSections.pairedBulls ? (
                      <p className="info-detail truncated">
                        {bullcard.pairedBulls.substring(0, maxLength)}...
                      </p>
                    ) : (
                      <p className="info-detail">{bullcard.pairedBulls}</p>
                    )}
                  </div>

                  {shouldHidePairedBulls && (
                    <button
                      className="info-toggle-btn"
                      onClick={() => toggleSection("pairedBulls")}
                    >
                      <i
                        className={`pi ${
                          expandedSections.pairedBulls
                            ? "pi-chevron-up"
                            : "pi-chevron-down"
                        }`}
                      />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Operators (with dropdown) */}
            <div className="info-card-wrapper">
              <div className="info-card">
                <div className="info-icon operators">
                  <i className="pi pi-users" />
                </div>

                <div className="info-text">
                  <span className="info-title">Operators</span>

                  {shouldHideOperators && !expandedSections.operators ? (
                    <p className="info-detail truncated">
                      {mergedOperatorString.substring(0, maxLength)}...
                    </p>
                  ) : (
                    <>
                      <p className="info-detail">
                        <strong>Operator 1:</strong> {bullcard.operator1 || "—"}
                      </p>
                      <p className="info-detail">
                        <strong>Operator 2:</strong> {bullcard.operator2 || "—"}
                      </p>
                    </>
                  )}
                </div>

                {shouldHideOperators && (
                  <button
                    className="info-toggle-btn"
                    onClick={() => toggleSection("operators")}
                  >
                    <i
                      className={`pi ${
                        expandedSections.operators
                          ? "pi-chevron-up"
                          : "pi-chevron-down"
                      }`}
                    />
                  </button>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default BullCard;
